/** Human-friendly renderers for the three artifact kinds produced by the
 * vertical AI slice: product_spec, engineering_plan, qa_report, plus a
 * release_bundle summary. */

export function ProductSpecView({ content }) {
  const ac = content.acceptance_criteria || [];
  const risks = content.risks || [];
  const oq = content.open_questions || [];
  return (
    <div>
      <h3 style={{ margin: "0 0 6px" }}>{content.title}</h3>
      <p className="hint" style={{ marginTop: 0 }}>{content.summary}</p>
      <div className="section-heading"><h3>Acceptance criteria</h3><span className="hint">{ac.length}</span></div>
      {ac.map((c) => (
        <div className="wide-row" key={c.id}>
          <div>
            <b className="mono" style={{ color: "var(--cyan)" }}>{c.id}</b>
            <p style={{ marginTop: 4 }}>{c.criterion}</p>
          </div>
        </div>
      ))}
      {risks.length ? (
        <>
          <div className="section-heading"><h3>Risks</h3><span className="hint">{risks.length}</span></div>
          {risks.map((r, i) => (
            <div className="wide-row" key={i}><div><b>{r.risk}</b><p>Mitigation: {r.mitigation}</p></div></div>
          ))}
        </>
      ) : null}
      {oq.length ? (
        <>
          <div className="section-heading"><h3>Open questions</h3><span className="hint">{oq.length}</span></div>
          <ul className="hint" style={{ paddingLeft: 18 }}>{oq.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </>
      ) : null}
    </div>
  );
}

export function EngineeringPlanView({ content }) {
  const c = content || {};
  return (
    <div>
      <h3 style={{ margin: "0 0 6px" }}>Implementation approach</h3>
      <p className="hint" style={{ marginTop: 0 }}>{c.approach}</p>

      {c.key_decisions?.length ? (
        <>
          <div className="section-heading"><h3>Key decisions</h3></div>
          {c.key_decisions.map((d, i) => (
            <div className="wide-row" key={i}><div><b>{d.decision}</b><p>Rationale: {d.rationale}</p></div></div>
          ))}
        </>
      ) : null}

      {c.modules?.length ? (
        <>
          <div className="section-heading"><h3>Modules</h3></div>
          {c.modules.map((m, i) => (
            <div className="wide-row" key={i}><div><b className="mono">{m.name}</b><p>{m.purpose}</p></div></div>
          ))}
        </>
      ) : null}

      {c.api_surface?.length ? (
        <>
          <div className="section-heading"><h3>API surface</h3></div>
          {c.api_surface.map((a, i) => (
            <div className="wide-row" key={i}>
              <div>
                <b className="mono"><span style={{ color: "var(--cyan)" }}>{a.method}</span> {a.path}</b>
                <p>{a.summary}</p>
              </div>
            </div>
          ))}
        </>
      ) : null}

      {c.schema_changes?.length ? (
        <>
          <div className="section-heading"><h3>Schema changes</h3></div>
          <ul className="hint" style={{ paddingLeft: 18 }}>{c.schema_changes.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </>
      ) : null}

      {c.criteria_coverage?.length ? (
        <>
          <div className="section-heading"><h3>Criteria coverage</h3><span className="hint">{c.criteria_coverage.length}</span></div>
          {c.criteria_coverage.map((cc, i) => (
            <div className="wide-row" key={i}>
              <div>
                <b className="mono" style={{ color: "var(--cyan)" }}>{cc.ac_id}</b>
                <p><b>Addressed by:</b> {cc.addressed_by}</p>
                <small className="hint">{cc.notes}</small>
              </div>
            </div>
          ))}
        </>
      ) : null}

      <div className="grid-2" style={{ marginTop: 12 }}>
        <div className="wide-row"><div><b>Test plan</b><p>{c.test_plan_summary}</p></div></div>
        <div className="wide-row"><div><b>Effort estimate</b><p>{c.effort_estimate}</p></div></div>
      </div>
    </div>
  );
}

export function QaReportView({ content }) {
  const c = content || {};
  const tone = c.verdict === "pass" ? "green" : c.verdict === "fail" ? "red" : "amber";
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span className={`pill ${tone}`} style={{ fontSize: 12 }}>{c.verdict}</span>
        <span className="hint">Independent evaluation. Not influenced by Engineering's own claims.</span>
      </div>
      <p style={{ marginTop: 12 }}>{c.overall_rationale}</p>

      <div className="section-heading"><h3>Per-criterion</h3><span className="hint">{c.per_criterion?.length || 0}</span></div>
      {(c.per_criterion || []).map((pc, i) => (
        <div className="wide-row" key={i}>
          <div>
            <b className="mono" style={{ color: "var(--cyan)" }}>{pc.ac_id}</b>{" "}
            <span className={`pill ${pc.status === "pass" ? "green" : pc.status === "fail" ? "red" : "amber"}`}>{pc.status}</span>
            <p>{pc.rationale}</p>
            <small className="hint">Evidence: {pc.evidence}</small>
          </div>
        </div>
      ))}

      {c.recommendations?.length ? (
        <>
          <div className="section-heading"><h3>Recommendations</h3></div>
          <ul className="hint" style={{ paddingLeft: 18 }}>{c.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </>
      ) : null}
    </div>
  );
}

export function ReleaseBundleView({ content }) {
  const c = content || {};
  return (
    <div>
      <h3 style={{ margin: "0 0 6px" }}>Release evidence bundle</h3>
      <p className="hint">All artifacts + QA verdict frozen for Director acceptance.</p>
      <div className="grid-2" style={{ marginTop: 10 }}>
        <div className="wide-row"><div><b>Product</b><p className="mono" style={{ fontSize: 11 }}>{c.product_artifact_id}</p><small>hash {c.product_artifact_hash?.slice(0, 16)}…</small></div></div>
        <div className="wide-row"><div><b>Engineering</b><p className="mono" style={{ fontSize: 11 }}>{c.engineering_artifact_id}</p><small>hash {c.engineering_artifact_hash?.slice(0, 16)}…</small></div></div>
        <div className="wide-row"><div><b>QA</b><p className="mono" style={{ fontSize: 11 }}>{c.qa_run_id}</p><small>verdict: {c.qa_verdict}</small></div></div>
        <div className="wide-row"><div><b>Assembled at</b><p>{c.assembled_at}</p></div></div>
      </div>
    </div>
  );
}

const RENDERERS = {
  product_spec: ProductSpecView,
  engineering_plan: EngineeringPlanView,
  qa_report: QaReportView,
  release_bundle: ReleaseBundleView,
};

export default function ArtifactView({ artifact }) {
  const Comp = RENDERERS[artifact.kind];
  if (!Comp) {
    return <pre className="mono hint" style={{ fontSize: 11, whiteSpace: "pre-wrap" }}>{JSON.stringify(artifact.content, null, 2)}</pre>;
  }
  return <Comp content={artifact.content} />;
}
